import { ref } from 'vue';
import { useModal, useModalSlot, VueFinalModal } from 'vue-final-modal';
import { useCustomToast } from '@/shared/lib';
import {
  getRecordControl,
  listRecordChangelog,
  RecordControlModal,
  RecordHistoryModal,
} from '@/widgets/record-modals';
import type {
  RecordChangelogEntry,
  RecordControl,
  RecordModalTarget,
} from '@/widgets/record-modals';

const MODAL_CENTERED_CLASS = 'flex justify-center items-center';

const buildDialogAttrs = (ariaLabel: string) => ({
  class: MODAL_CENTERED_CLASS,
  'aria-label': ariaLabel,
  overlayTransition: 'vfm-fade',
  contentTransition: 'vfm-fade',
});

const EMPTY_TARGET: RecordModalTarget = { id: 0, code: '', title: null, revision: 0 };

const EMPTY_CONTROL: RecordControl = {
  id: 0,
  code: '',
  title: null,
  revision: null,
  elaboration: { memberId: null, memberName: null, date: null },
  approval: null,
  verification: null,
  effectiveDate: null,
};

const RECORD_HISTORY_ERROR_MESSAGE =
  'Não foi possível carregar o histórico deste registro. Tente novamente.';

const RECORD_CONTROL_ERROR_MESSAGE =
  'Não foi possível carregar o controle do registro. Tente novamente.';

export function useMatrixRevisionModals() {
  const toast = useCustomToast();

  const buildHistorySlot = (target: RecordModalTarget, entries: RecordChangelogEntry[]) =>
    useModalSlot({
      component: RecordHistoryModal,
      attrs: {
        target,
        entries,
        onClose: () => {
          historyModal.close();
        },
      },
    });

  const historyModal = useModal({
    component: VueFinalModal,
    attrs: buildDialogAttrs('Histórico das Atualizações (changelog)'),
    slots: { default: buildHistorySlot(EMPTY_TARGET, []) },
  });

  const openHistory = async (target: RecordModalTarget) => {
    try {
      const entries = await listRecordChangelog(target.id);
      historyModal.patchOptions({ slots: { default: buildHistorySlot(target, entries) } });
      await historyModal.open();
    } catch (error) {
      toast.showErrorToast({ title: RECORD_HISTORY_ERROR_MESSAGE, type: 'error' });
      console.error('Erro ao carregar o histórico do registro:', error);
    }
  };

  const buildControlSlot = (target: RecordModalTarget, control: RecordControl) =>
    useModalSlot({
      component: RecordControlModal,
      attrs: {
        target,
        control,
        onClose: () => {
          controlModal.close();
        },
      },
    });

  const controlModal = useModal({
    component: VueFinalModal,
    attrs: buildDialogAttrs(
      'Controle de Elaboração, Aprovação, Verificação e Homologação de Registros'
    ),
    slots: { default: buildControlSlot(EMPTY_TARGET, EMPTY_CONTROL) },
  });

  const isControlLoading = ref(false);

  const openControl = async (target: RecordModalTarget) => {
    isControlLoading.value = true;

    try {
      const control = await getRecordControl(target.id);
      controlModal.patchOptions({ slots: { default: buildControlSlot(target, control) } });
      await controlModal.open();
    } catch (error) {
      toast.showErrorToast({ title: RECORD_CONTROL_ERROR_MESSAGE, type: 'error' });
      console.error('Erro ao carregar o controle do registro:', error);
    } finally {
      isControlLoading.value = false;
    }
  };

  return {
    openHistory,
    openControl,
    isControlLoading,
  };
}
